import { useCallback, useRef, useState } from 'react'

/**
 * A form field seeded from the server that stops following it once a human
 * touches it.
 *
 * It exists because the console shipped this bug and would have shipped it
 * again. The start-session dialog let project→account continuity overwrite the
 * account an operator had just chosen, and sessions ran on somebody else's
 * subscription without a word on screen. The shape of that defect is not
 * specific to accounts: *anything* that seeds a field from server data and then
 * re-seeds it can discard a decision a person already made.
 *
 * The hazard also grew. The console now updates over the socket and refetches on
 * window focus, so background data arrives constantly — and it arrives most
 * often at exactly the moment an operator tabs back to finish typing.
 *
 * The rule this encodes, and the only rule that is safe:
 *
 *   **A prefill may fill a field nobody has set. It may never overwrite one a
 *   human has set.**
 *
 * `dirty` reports whether the human has taken it over, so a caller can stop
 * showing a "this came from your last session" hint that is no longer true.
 * `reset` gives the field back to the server, which is what a successful save
 * means: what was typed is now what the server holds.
 *
 * Do not reach for `useEffect(() => setX(server), [server])` instead. That is
 * the pattern this replaces, and the bug is invisible in it — it looks like
 * keeping the form in step, and reads as such right up until somebody's input
 * disappears.
 */
export function useEditable<T>(serverValue: T): {
  value: T
  set: (next: T) => void
  dirty: boolean
  reset: () => void
} {
  const [local, setLocal] = useState<T | undefined>(undefined)
  // A ref as well as state, so a caller reading `dirty` inside a callback sees
  // the current answer rather than the one from the render it was created in.
  const touched = useRef(false)
  const [dirty, setDirty] = useState(false)

  const set = useCallback((next: T) => {
    touched.current = true
    setDirty(true)
    setLocal(next)
  }, [])

  const reset = useCallback(() => {
    touched.current = false
    setDirty(false)
    setLocal(undefined)
  }, [])

  return {
    value: touched.current && local !== undefined ? local : serverValue,
    set,
    dirty,
    reset,
  }
}
